import { useTranslation } from 'react-i18next';
import { DATAGRID__NO_ROWS } from 'translations/constants';
import { useAppSelector } from 'store';
import { itemsPerPageSelector } from 'store/selectors/app-selectors';
import { UPDataGridProps } from './types';
import { DataGridStyled } from './styles';
import CustomPagination from './CustomPagination';

const UPDataGrid = (props: UPDataGridProps): JSX.Element => {
  const {
    rows,
    columns,
    setRowOnHover,
    onSelectionModelChange,
    withCheckboxes,
    hideFooter,
    getRowId,
    experimentalFeatures,
    onCellEditStop,
    onCellEditStart,
    selectionModel,
    disableSelectionOnClick,
    sortModel,
    handleSortModelChange,
    onHeaderColumnClick,
    getRowClassName,
    headerHeight,
    autoHeight,
    onPageSizeChange,
    onPageChange,
    sx,
    onCellEditCommit,
    getRowHeight,
    isCellEditable,
  } = props;

  const [t] = useTranslation();
  const itemsPerPage = useAppSelector(itemsPerPageSelector);

  return (
    <DataGridStyled
      rows={rows}
      columns={columns}
      checkboxSelection={withCheckboxes}
      disableColumnMenu
      getRowId={getRowId}
      componentsProps={{
        row: {
          onMouseEnter: (event: { currentTarget: { dataset: { id: any } } }) => {
            // Numeric row ids stay numbers, so every existing grid behaves exactly as before.
            // Grids whose getRowId returns a composite string (e.g. `Application-41`, used to
            // keep ids from two source tables from colliding) would become NaN under a blind
            // Number() and never match params.id, so those pass through as-is.
            const { id } = event.currentTarget.dataset;
            const numericId = Number(id);
            setRowOnHover(id !== undefined && id !== '' && !Number.isNaN(numericId) ? numericId : id);
          },
        },
      }}
      selectionModel={selectionModel}
      disableSelectionOnClick={disableSelectionOnClick}
      onSelectionModelChange={onSelectionModelChange}
      hideFooter={hideFooter}
      experimentalFeatures={experimentalFeatures}
      onCellEditStop={onCellEditStop}
      onCellEditStart={onCellEditStart}
      localeText={{ noRowsLabel: t(DATAGRID__NO_ROWS) }}
      pagination
      pageSize={itemsPerPage}
      onPageSizeChange={onPageSizeChange}
      onPageChange={onPageChange}
      components={{ Pagination: CustomPagination }}
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      onColumnHeaderClick={onHeaderColumnClick}
      headerHeight={headerHeight ?? 56} // 56 is the default header height
      autoHeight={autoHeight}
      getRowClassName={getRowClassName}
      sx={sx}
      onCellEditCommit={onCellEditCommit}
      getRowHeight={getRowHeight}
      isCellEditable={isCellEditable}
    />
  );
};

UPDataGrid.defaultProps = {
  withCheckboxes: true,
  getRowId: null,
  hideFooter: false,
  experimentalFeatures: null,
  onCellEditStop: null,
  onCellEditStart: null,
};

export default UPDataGrid;
